import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Button, Popup, PopupActionList, PopupSearchInput, Text } from '@ui';
import { translate } from '@utils/i18n';
import './ActionMenuStyles.scss';
const SEARCH_NAMESPACE = 'ui.select_input';
/** Minimum item count before the in-menu search box appears (Alana: "after 10 there's a search"). */
const SEARCH_THRESHOLD = 10;
/**
 * ActionMenu
 *
 * Generic trigger → popup → list-of-links menu built on the QUI `Popup`
 * toolkit. Each item is a real anchor (`<a href>`), so navigation keeps
 * middle-click / open-in-new-tab. Optional `searchable` adds a client-side
 * label filter for long lists. ERB-mountable via `react_component 'ActionMenu'`
 * and importable by other features from `@features/ActionMenu`.
 *
 * @version 1.0.0
 * @since 2026-08-03
 */
export function ActionMenu({ items, triggerLabel, triggerIcon = 'fa-light fa-plus', variant = 'button', searchable = true, searchPlaceholder, visible = true, position = 'bottom-right', mode = 'light' }) {
    const anchorRef = useRef(null);
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const handleToggle = useCallback(() => setOpen((prev) => !prev), []);
    const handleClose = useCallback(() => {
        setOpen(false);
        setQuery('');
    }, []);
    const showSearch = searchable && items.length >= SEARCH_THRESHOLD;
    const visibleItems = useMemo(() => {
        if (!showSearch || query.trim() === '')
            return items;
        const needle = query.toLowerCase();
        return items.filter((item) => item.label.toLowerCase().includes(needle));
    }, [items, query, showSearch]);
    if (visible !== true || items.length === 0)
        return null;
    const isIcon = variant === 'icon';
    const resolvedPlaceholder = searchPlaceholder !== null && searchPlaceholder !== void 0 ? searchPlaceholder : translate('search_placeholder', { namespace: SEARCH_NAMESPACE });
    return (React.createElement("div", { className: "action-menu" },
        React.createElement(Button, { ref: anchorRef, mode: mode, className: "action-menu__trigger", onClick: handleToggle, "aria-haspopup": "menu", "aria-expanded": open, "aria-label": triggerLabel, title: triggerLabel, "data-testid": "action-menu-trigger" },
            React.createElement("i", { className: triggerIcon, "aria-hidden": "true" }),
            !isIcon && triggerLabel && React.createElement("span", { className: "action-menu__trigger-label" }, triggerLabel)),
        React.createElement(Popup, { anchor: anchorRef.current, show: open, onClose: handleClose, position: position, collision: showSearch ? undefined : { horizontal: 'fit', vertical: 'flip' } },
            showSearch && React.createElement(PopupSearchInput, { value: query, onChange: setQuery, placeholder: resolvedPlaceholder }),
            React.createElement(Popup.Section, { fillHeight: true },
                React.createElement(PopupActionList, null, visibleItems.map((item) => {
                    const content = (React.createElement(React.Fragment, null,
                        item.icon && React.createElement("i", { className: item.icon, "aria-hidden": "true" }),
                        React.createElement(Text, { as: "span", variant: "body", className: "action-menu__item-label" }, item.label)));
                    if (item.event) {
                        const { name, detail } = item.event;
                        return (React.createElement(Button, { key: item.key, mode: mode, role: "menuitem", className: "qmb-ui-popup__action action-menu__item", "data-testid": `action-menu-item-${item.key}`, onClick: () => {
                                document.dispatchEvent(new CustomEvent(name, { detail }));
                                handleClose();
                            } }, content));
                    }
                    return (React.createElement(Text, { key: item.key, as: "a", href: item.href, role: "menuitem", target: item.target, rel: item.target === '_blank' ? 'noopener noreferrer' : undefined, className: "qmb-ui-popup__action action-menu__item", "data-testid": `action-menu-item-${item.key}`, onClick: handleClose }, content));
                }))))));
}
ActionMenu.displayName = 'ActionMenu';
